import {
  useRedirectPrompt,
  useRouteSummary
} from '$contexts/RouteContext/hooks'
import { redirect, ROUTE_NAMES } from '$router/config'
import { getQueryUserByEmail } from '$services/api'
import { flexColumn, flexRow } from '$styles/common.css'
import { Banner, Button, Spinner } from '@genie-fintech/ui/components'
import { BaseText } from '@genie-fintech/ui/components/fields'
import { Icon } from '@genie-fintech/ui/icons'
import { themeVars } from '@genie-fintech/ui/style/theme'
import { zodResolver } from '@hookform/resolvers/zod'
import { useRequest } from 'ahooks'
import { ArrowRight, ChevronLeft, CornerDownLeft, XCircle } from 'lucide-react'
import { FC, useCallback, useEffect, useState } from 'react'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { z } from 'zod'
import { modalContentBlock, modalContentCard } from '../styles.css'
import {
  footnote,
  title as titleStyle
} from '@genie-fintech/ui/style/typography'
import { COMPANY_NAME } from '$constants/themeTexts'

const { colors } = themeVars

enum USER_STATUS {
  NEW_USER = 'new_user',
  CURRENT_APP_USER = 'current_app_user',
  OTHER_APP_USER = 'other_app_user'
}

const schema = z.object({
  email: z.string().trim().min(1, { message: 'Required!' }).email()
})

type TFormValues = z.infer<typeof schema>

interface IProps {
  onSuccess?: (email: string, userId?: string) => void
  email: string
  onBack: VoidFunction
}

const checkUserStatus = (userStatus: string) => {
  if (userStatus.includes('Old')) return USER_STATUS.CURRENT_APP_USER
  if (userStatus.includes('Exists')) return USER_STATUS.OTHER_APP_USER
  return USER_STATUS.NEW_USER
}

type TQueryUser = Awaited<ReturnType<typeof getQueryUserByEmail>>['data']

const QueryUser: FC<IProps> = ({ onSuccess, email, onBack }) => {
  const [queryUser, setQueryUser] = useState<TQueryUser>()

  const { route } = useRouteSummary()
  const { appId } = route.params

  const { runAsync: getQueryUserAsync, loading: queryingUser } = useRequest(
    getQueryUserByEmail,
    {
      manual: true,
      onSuccess: ({ data }) => {
        setQueryUser(data)
      }
    }
  )

  const { id: userId, status = '' } = { ...queryUser }

  const userStatus = checkUserStatus(status)

  const {
    handleSubmit,
    control,
    formState: { isDirty },
    reset
  } = useForm<TFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      email
    }
  })

  useRedirectPrompt({ shouldPrompt: isDirty })

  useEffect(() => {
    if (!isDirty) return
    setQueryUser(undefined)
  }, [isDirty])

  const emailValue = useWatch({ name: 'email', control })

  const onAddUser = useCallback(() => {
    onSuccess?.(emailValue, userId)
  }, [onSuccess, emailValue, userId])

  const onViewUser = useCallback(() => {
    redirect(ROUTE_NAMES.APP_USER_DETAIL, ({ params, ...options }) => ({
      ...options,
      params: { ...params, userId }
    }))
  }, [userId])

  const onSubmit = handleSubmit((values: TFormValues) => {
    if (!appId) return

    getQueryUserAsync({ application_id: appId, ...values }).then(({ data }) => {
      reset({ email: values.email })
      const _status = checkUserStatus(data.status)
      if (_status === USER_STATUS.NEW_USER) {
        onAddUser()
      }
    })
  })

  const showAddUserButton = userStatus === USER_STATUS.NEW_USER

  return (
    <form onSubmit={onSubmit} className={flexColumn}>
      <article className={modalContentBlock}>
        <div>
          <Button
            styleVariants={{ kind: 'neutral', type: 'outlined', size: 'small' }}
            style={{ background: colors.area.high }}
            onClick={onBack}
            disabled={queryingUser}
          >
            <ChevronLeft size={16} />
            Back to Listing
          </Button>
        </div>

        <article className={modalContentCard}>
          <article className={flexColumn} style={{ gap: 4 }}>
            <p className={titleStyle.two}>Add new user or query with email</p>
            <p className={footnote.one} style={{ color: colors.neutral[60] }}>
              By inputting the user's email to query or add user into this
              application.
            </p>
          </article>

          <Controller
            name="email"
            control={control}
            render={({ field, fieldState: { error } }) => {
              return (
                <BaseText
                  elementControlProps={{ style: { padding: '20px 24px' } }}
                  affix={{
                    pre: <Icon namespace="Search" width={16} />,
                    post: (
                      <article
                        style={{
                          display: 'flex',
                          gap: 4,
                          flexDirection: 'row-reverse'
                        }}
                      >
                        <article
                          className={flexRow}
                          style={{ color: colors.text.disabled, gap: 4 }}
                        >
                          <CornerDownLeft size={14} />
                          <span style={{ fontSize: 12 }}>Enter</span>
                        </article>

                        {!!field.value && (
                          <button
                            type="button"
                            onClick={() => field.onChange('')}
                            style={{ color: colors.text.light }}
                          >
                            <XCircle size={20} />
                          </button>
                        )}
                      </article>
                    )
                  }}
                  inputProps={{
                    type: 'text',
                    value: field.value,
                    onChange: field.onChange,
                    placeholder: 'Search with email here...'
                  }}
                  error={!!error?.message}
                  message={error?.message}
                />
              )
            }}
          />

          {userStatus === USER_STATUS.OTHER_APP_USER && (
            <Banner
              border
              kind="success"
              type="transparent"
              title="Do you want to add this user?"
              description={`This user is already in the ${COMPANY_NAME} system!`}
              suffix={
                <Button
                  styleVariants={{ size: 'small', kind: 'success' }}
                  children="Add"
                  onClick={onAddUser}
                />
              }
            />
          )}

          {userStatus === USER_STATUS.CURRENT_APP_USER && (
            <Banner
              border
              kind="warning"
              type="transparent"
              title="You can not add this user!"
              description="This user is already in the current application and it will be duplicate data in one application!"
              suffix={
                <Button
                  styleVariants={{ size: 'small', kind: 'warning' }}
                  onClick={onViewUser}
                >
                  Go to user details
                  <ArrowRight size={16} />
                </Button>
              }
            />
          )}

          <footer className={flexRow} style={{ justifyContent: 'flex-end' }}>
            {showAddUserButton && (
              <Button type="submit" disabled={queryingUser}>
                {queryingUser && <Spinner />}
                Add User
              </Button>
            )}
          </footer>
        </article>
      </article>
    </form>
  )
}

export default QueryUser
